import { useAsync } from '../../components/common';
import { api } from '../../session';
import type { AuditRecord, Discovery } from '../../api';
import { joinParts, type Issue } from './shared';

// Admin: the registry, what discovery found and what it could not, and the last 24 h of the audit log.

export interface AdminSummary {
  clusters: { total: number; discovered: number; disabled: number };
  environments: { registered: number; namespaces: number };
  servers: { total: number; linked: number; noPassword: number };
  discovery: Discovery | null;
  discoveryError: string | null;
  /** Postgres instances discovered in GCP that have no credentials (no DB tools for them). */
  withoutCredentials: number;
  audit: {
    writes: number;
    errors: AuditRecord[];
    denied: AuditRecord[];
    recent: AuditRecord[];
  };
}

const DAY = 24 * 3600_000;

async function load(): Promise<AdminSummary> {
  const [clusters, registered, all, servers, audit, discovery] = await Promise.all([
    api.admin.clusters.list(),
    api.admin.environments.list(),
    api.environments(),
    api.admin.dbServers.list(),
    api.admin.audit({ limit: 500 }),
    api.admin.discovery().then(
      (d) => ({ d, e: null as string | null }),
      (e: Error) => ({ d: null, e: e.message })
    ),
  ]);
  const since = Date.now() - DAY;
  const day = audit.filter((a) => Date.parse(a.occurredAt) >= since);
  const linked = new Set(servers.map((s) => s.instance).filter(Boolean));
  return {
    clusters: {
      total: clusters.length,
      discovered: clusters.filter((c) => c.source === 'discovered').length,
      disabled: clusters.filter((c) => !c.enabled).length,
    },
    environments: { registered: registered.length, namespaces: all.filter((e) => e.discovered).length },
    servers: {
      total: servers.length,
      linked: servers.filter((s) => s.instance).length,
      noPassword: servers.filter((s) => !s.hasPassword).length,
    },
    discovery: discovery.d,
    discoveryError: discovery.e,
    withoutCredentials: (discovery.d?.instances ?? []).filter(
      (i) => i.engine === 'postgres' && !linked.has(i.resourceName)
    ).length,
    audit: {
      writes: day.filter((a) => a.action !== 'login').length,
      errors: day.filter((a) => a.outcome === 'error'),
      denied: day.filter((a) => a.outcome === 'denied'),
      recent: audit.slice(0, 10),
    },
  };
}

/** Admin summary (ophub:admin only); refreshed by the caller. */
export function useAdminSummary(enabled: boolean) {
  return useAsync(() => (enabled ? load() : Promise.resolve(null)), [enabled]);
}

export function adminIssues(s: AdminSummary): Issue[] {
  const audit = (a: AuditRecord, status: string): Issue => ({
    key: `audit:${a.id}`,
    status,
    name: `${a.userName || a.userSub}: ${a.action}`,
    what: `${a.targetType} ${a.targetId}`.trim(),
    detail: joinParts([a.outcome, a.detail, new Date(a.occurredAt).toLocaleTimeString()]),
    to: `/admin/audit?targetType=${encodeURIComponent(a.targetType)}`,
  });
  return [
    ...(s.discoveryError
      ? [
          {
            key: 'disc',
            status: 'error',
            name: 'GCP discovery',
            what: 'discovery',
            detail: s.discoveryError,
            to: '/admin/db-servers',
          },
        ]
      : []),
    ...(s.discovery?.errors ?? []).map((e, i) => ({
      key: `disc:${i}`,
      status: 'warn',
      name: e.projectId || 'project search',
      what: `discovery · ${e.api}`,
      detail: e.message,
      to: '/admin/db-servers',
    })),
    ...s.audit.errors.slice(0, 10).map((a) => audit(a, 'error')),
    ...s.audit.denied.slice(0, 10).map((a) => audit(a, 'warn')),
  ];
}
